'use client';

import React, { useMemo } from 'react';
import useNewGetPayerMix from 'src/hooks/payerMix/useNewGetPayerMix';
import WidgetLoading from 'src/components/Profiles/shared/widgets/shared/WidgetLoading';
import WidgetEmpty from 'src/components/Profiles/shared/widgets/shared/WidgetEmpty';
import { useProfile } from 'src/context/providers/ProfileProvider';
import { useWidgetConfig } from 'src/context/providers/WidgetConfigProvider';
import { getEntityIdentifiers } from 'src/utils/getEntityIdentifiers';
import PreviewInlineBar from 'src/components/Profiles/shared/widgets/shared/PreviewInlineBar';

const PREVIEW_MAX_ITEMS = 2;

const CompactPayerMixPreview: React.FC = () => {
  const { entityDetails } = useProfile();
  const widgetConfig = useWidgetConfig();

  const { providerId, profileType } = getEntityIdentifiers(entityDetails);
  const filterSetId = widgetConfig?.filterSet?.id;

  const { data: payerMixByName, isLoading } = useNewGetPayerMix({
    providerId,
    groupBy: 'payer_name',
    location: profileType,
    filterSetId,
  });

  const topPayers = useMemo(() => {
    if (!payerMixByName?.results) return [];
    return [...payerMixByName.results]
      .sort(
        (a: Record<string, any>, b: Record<string, any>) =>
          b.percentage - a.percentage
      )
      .slice(0, PREVIEW_MAX_ITEMS);
  }, [payerMixByName]);

  if (isLoading || widgetConfig?.isLoading) {
    return <WidgetLoading data-testid="compact-payer-mix-preview-loading" aria-label="Loading payer mix" />;
  }

  if (topPayers.length === 0) {
    return <WidgetEmpty data-testid="compact-payer-mix-preview-empty" />;
  }

  return (
    <div
      className="flex flex-col gap-2"
      data-testid="compact-payer-mix-preview"
    >
      {topPayers.map((payer: Record<string, any>) => (
        <PreviewInlineBar
          key={payer.payer_name}
          label={payer.payer_name}
          value={payer.percentage}
          maxValue={100}
          formattedValue={`${Math.round(payer.percentage)}%`}
        />
      ))}
    </div>
  );
};

export default CompactPayerMixPreview;
